import { addToast, ErrorToast, Toastß } from '@otter-ai/den-components';
import { getDesktopAppUtmContent } from '@otter-ai/desktop';
import type { Plan } from '@otter-ai/recall-client';
import {
  LimitStatus,
  PremiumFeature,
  PurchaseOpenAccountPlanUpgradeEvent,
  UpgradePlanType,
  UpgradeTrigger,
} from '@otter-ai/telemetry';
import { PURCHASE_PLAN_UPGRADE_ANALYTICS } from '@otter-ai/zone-purchase/src/lib/DO_NOT_USE';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import {
  AppsSettingsOauthRedirectSource,
  DROPBOX_APPS_SETTINGS_OAUTH_REDIRECT_PATH,
  DROPBOX_OAUTH_URL,
} from '@/constants/auth';
import {
  API_STATUS_FAILED,
  CODE_MISSING_INPUT,
  CODE_VALIDATION_ERROR,
} from '@/constants/error-codes';
import { PATH_URL } from '@/constants/path-constants';
import { useDesktopAutoConnect } from '@/desktop/providers/DesktopAutoConnectProvider';
import { legacyRestFetch } from '@/features/account-settings/legacyRestFetch';
import { subscriptionQuery } from '@/query/subscriptionQueryCreator';
import { teamAppsQueryCreator } from '@/query/teamAppsQueryCreator';
import { userFeaturesQuery } from '@/query/userFeaturesQueryCreator';
import { userProfileQueryCreator } from '@/query/userProfileQueryCreator';
import { userWorkspaceQueryCreator } from '@/query/userWorkspaceQueryCreator';
import { workspaceQueryCreator } from '@/query/workspaceQueryCreator';
import { openOAuthUrl } from '@/shared/utils/auth';
import { setInLocalStorage } from '@/shared/utils/localStorageUtils';
import { isFreeTeamPlan } from '@/shared/utils/planUtils';
import { getConnectedAppsQueryCreator } from '../query/getConnectedAppsQueryCreator';
import { trackDropbox } from '../tracking/dropbox-tracking';

export type ConnectDropboxToOtterAccountVars = {
  /**
   * The code returned from the Dropbox OAuth callback.
   */
  code: string;
  /**
   * The redirect URL to redirect to after the connection is made.
   */
  redirectUrl?: string;
};

export type DropboxOAuthHook = {
  /**
   * Opens the Dropbox OAuth page for the user to connect their Dropbox account to Otter.
   */
  openDropboxAuthorizationPage: (redirectUri?: string) => void;
  /**
   * After the Dropbox OAuth redirects back to Otter, call this function to
   * connect the user's Dropbox account to Otter.
   */
  connectDropboxToOtterAccountAsync: (
    vars: ConnectDropboxToOtterAccountVars,
  ) => Promise<void>;
  connectDropboxToOtterAccount: (
    vars: ConnectDropboxToOtterAccountVars,
  ) => void;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  // dropboxServiceType: ReturnType<typeof dropboxServiceType>;
  /**
   * Call this function to prefetch data needed to open the Dropbox authorization page.
   */
  prefetchData: () => void;
};

/**
 * A collection of functions for the user to connect their Dropbox account to Otter.
 */
export function useDropboxOAuth(): DropboxOAuthHook {
  const t = useTranslations('settings:integrations.apps.dropbox');
  const queryClient = useQueryClient();
  const router = useRouter();

  const openDropboxAuthorizationPage = async (redirectUri?: string) => {
    const [{ allow_dropbox_sync }, { plan }] = await Promise.all([
      queryClient.fetchQuery(userFeaturesQuery()),
      queryClient.fetchQuery(subscriptionQuery()),
    ]);

    if (allow_dropbox_sync) {
      openDropboxOAuth(redirectUri);
      return;
    }

    if (isFreeTeamPlan(plan as unknown as Plan)) {
      // TODO(sleahy): Throw as an error for the caller to handle
      addToast(<Toastß description={t('businessSubscriptionExpired')} />);
      return;
    }

    const tracking = PurchaseOpenAccountPlanUpgradeEvent({
      UpgradePlanType: UpgradePlanType.team,
      PremiumFeature: PremiumFeature.allowDropboxSync,
      LimitStatus: LimitStatus.limitReached,
      UpgradeTrigger: UpgradeTrigger.featureInteraction,
    });
    setInLocalStorage(
      PURCHASE_PLAN_UPGRADE_ANALYTICS,
      tracking.properties,
      true,
    );
    trackDropbox.main.dropboxPlanUpgrade();

    const utmContent = getDesktopAppUtmContent();
    router.push(
      `${PATH_URL.PURCHASE_TEAM_PLAN}?utm_content=${encodeURIComponent(utmContent)}`,
    );
  };

  const { mutate, mutateAsync, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (vars: ConnectDropboxToOtterAccountVars) => {
      await connectDropboxToOtterAccount(vars);
    },
    onSuccess: () =>
      Promise.allSettled([
        queryClient.invalidateQueries(teamAppsQueryCreator()),
        queryClient.invalidateQueries(getConnectedAppsQueryCreator()),
        queryClient.invalidateQueries(userProfileQueryCreator()),
        queryClient.invalidateQueries(userWorkspaceQueryCreator()),
        queryClient.invalidateQueries(workspaceQueryCreator()),
      ]),
    onError: (error) => {
      if (error instanceof MissingDropboxAuthorizationCodeError) {
        addToast(<ErrorToast description={t('missingCode')} />);
      }
    },
  });

  const prefetchData = () => {
    queryClient.prefetchQuery(userFeaturesQuery());
    queryClient.prefetchQuery(subscriptionQuery());
  };

  // Auto-trigger OAuth if desktop app requested connecting this app
  useDesktopAutoConnect(
    AppsSettingsOauthRedirectSource.DROPBOX,
    openDropboxAuthorizationPage,
  );

  return {
    connectDropboxToOtterAccount: mutate,
    connectDropboxToOtterAccountAsync: mutateAsync,
    isError,
    isSuccess,
    isPending,
    prefetchData,
    openDropboxAuthorizationPage,
  };
}

function openDropboxOAuth(redirectUri?: string): void {
  const url = new URL(DROPBOX_OAUTH_URL);
  url.searchParams.set('client_id', getDropboxClientId());
  url.searchParams.set('response_type', 'code');
  // TODO(OTWEB-9102): Remove `state` param or use it for security.
  url.searchParams.set(
    'state',
    process.env.NEXT_PUBLIC_DROPBOX_OAUTH_STATE ?? '123',
  );
  url.searchParams.set('redirect_uri', redirectUri ?? defaultRedirectUrl());
  url.searchParams.set('token_access_type', 'offline');

  openOAuthUrl(url.toString(), AppsSettingsOauthRedirectSource.DROPBOX);
}

function getDropboxClientId() {
  if (!process.env.NEXT_PUBLIC_DROPBOX_CLIENT_ID) {
    throw new MissingDropboxConfigurationError('Client ID');
  }
  return process.env.NEXT_PUBLIC_DROPBOX_CLIENT_ID;
}

function defaultRedirectUrl() {
  return new URL(
    DROPBOX_APPS_SETTINGS_OAUTH_REDIRECT_PATH,
    process.env.NEXT_PUBLIC_DROPBOX_OAUTH_DEFAULT_REDIRECT_BASE ??
      location.href,
  ).toString();
}

async function connectDropboxToOtterAccount({
  code,
  redirectUrl,
}: {
  code: string;
  redirectUrl?: string;
}) {
  if (!code) {
    throw new MissingDropboxAuthorizationCodeError();
  }

  const body = new FormData();
  body.append('code', code);
  body.append('redirect_uri', redirectUrl ?? defaultRedirectUrl());

  const response = await legacyRestFetch('dropbox_connection', {
    method: 'POST',
    body,
  });

  if (!response.ok) {
    throw new Error('Failed to connect Dropbox to Otter account');
  }

  if (response.status !== 200) {
    throw response.body;
  }

  const results = await response.json();

  if (results.status === API_STATUS_FAILED) {
    if (results.code === CODE_MISSING_INPUT) {
      throw new FailedToConnectDropboxToOtterAccountError(
        'Missing input',
        results.code,
        results,
      );
    }
    if (results.code === CODE_VALIDATION_ERROR) {
      throw new FailedToConnectDropboxToOtterAccountError(
        'Validation error',
        results.code,
        results,
      );
    }
    throw new FailedToConnectDropboxToOtterAccountError(
      'Failed to connect Dropbox to Otter account',
      results.code,
      results,
    );
  }

  return results;
}

class MissingDropboxConfigurationError extends Error {
  name = 'MissingDropboxConfigurationError';

  constructor(missingConfig: string) {
    super(`Missing Dropbox configuration: ${missingConfig}`);
  }
}

class FailedToConnectDropboxToOtterAccountError extends Error {
  name = 'FailedToConnectDropboxToOtterAccountError';

  constructor(
    message: string,
    public errorCode: number,
    public source?: unknown,
  ) {
    super(message);
  }
}

export class MissingDropboxAuthorizationCodeError extends Error {
  name = 'MissingDropboxAuthorizationCodeError';

  constructor() {
    super('Dropbox authorization code is required');
  }
}
